/**
 * Single source of truth for "is this vehicle truly insured?"
 *
 * A vehicle is TRULY insured iff it has at least one vehicle_insurance row:
 *   - with removed_at IS NULL
 *   - whose parent insurance_contract has status === 'active'
 *
 * Any other state (VI on terminated/expired/suspended/cancelled contract,
 * VI soft-removed) counts as UNINSURED even though the VI row exists.
 *
 * Use this helper anywhere you compute "uninsured" — KPIs, alerts, filters
 * — so the UI stays consistent with each other and with the backend
 * coherence check (check_insurance_coherence.mjs §3).
 */

const CONTRACT_ACTIVE_STATUS = 'active';

// Contract types that cover exactly one vehicle (1:1). Adding a second
// vehicle makes no insurance sense — the whole policy is sized for one.
// Swapping the covered vehicle (mid-policy substitution) is a separate
// flow and will be handled post-salon.
export const SINGLE_VEHICLE_CONTRACT_TYPES = new Set([
  'motor_single',
  'machinery_breakage_single',
  'other_individual',
]);

export function isSingleVehicleContract(contract) {
  return !!contract && SINGLE_VEHICLE_CONTRACT_TYPES.has(contract.contract_type);
}

export function isContractActive(contract) {
  return !!contract && contract.status === CONTRACT_ACTIVE_STATUS;
}

/**
 * Build a Set of vehicle IDs that are currently covered by at least one
 * active vehicle_insurance row on an active insurance_contract.
 *
 * @param {Array} vehicleInsurances - rows from /vehicle_insurance
 * @param {Array} contracts         - rows from /insurance_contract
 * @returns {Set<number>} vehicle IDs that are truly insured
 */
export function buildTrulyInsuredIds(vehicleInsurances = [], contracts = []) {
  const activeContractIds = new Set(
    contracts.filter(isContractActive).map(c => c.id)
  );
  const insuredIds = new Set();
  for (const vi of vehicleInsurances) {
    if (vi.removed_at) continue;
    if (!activeContractIds.has(vi.contract_id)) continue;
    insuredIds.add(vi.vehicle_id);
  }
  return insuredIds;
}

/**
 * Resolve the canonical coverage_model for a vehicle_insurance row.
 * Source of truth = coverage_model_id (FK). Accepts a row already enriched
 * with a `coverage_model` object (backend join / enrichedContracts) OR a
 * coverageModels lookup list. NEVER reads the deprecated legacy coverage
 * string column — that dual read path is what kept the insurance schema
 * desynced (the recurring "garanties qui reviennent" bug).
 *
 * @param {object} vi              a vehicle_insurance row
 * @param {Array}  coverageModels  rows from /coverage_model (insuranceModuleAPI.getModels)
 * @returns {object|null} the coverage_model { id, code, name, level, … } or null
 */
export function resolveCoverageModel(vi, coverageModels = []) {
  if (!vi) return null;
  if (vi.coverage_model && typeof vi.coverage_model === 'object') return vi.coverage_model;
  if (vi.coverage_model_id == null) return null;
  return coverageModels.find(m => String(m.id) === String(vi.coverage_model_id)) || null;
}

/**
 * Canonical coverage level code for a row (e.g. 'tous_risques') or null.
 * Drop-in replacement for the legacy string column: coverage_model.level
 * carries the exact same lowercase enum values, so existing label/color maps
 * keyed on those values keep working.
 */
export function resolveCoverageLevel(vi, coverageModels = []) {
  return resolveCoverageModel(vi, coverageModels)?.level ?? null;
}

/** Canonical human label for a row: coverage_model.name, else null. */
export function resolveCoverageName(vi, coverageModels = []) {
  return resolveCoverageModel(vi, coverageModels)?.name ?? null;
}
